import React, { Fragment, useEffect, useRef, useState } from "react";
import { Card, Badge, OverlayTrigger, Tooltip, Button, Spinner } from "react-bootstrap";
import { Link } from "react-router-dom";
import { Star } from "lucide-react";
import { useDashboardByName, useDashboardsInCategories, useRecentDashboards } from "@/hooks/dashboard";
import { useStudentsAtRisk } from "@/hooks/students";
import { useDueAndUpcomingActionPlans } from "@/hooks/actionplans";
import { useSchoolSettings } from "@/hooks/schools";
import { Utils } from "@/common/utils";
import Plus from "Components/Icons/Plus";
import UserGroup from "Components/Icons/UserGroup";
import ClockRotateLeft from "Components/Icons/ClockRotateLeft";
import ArrowUpRightFromSquare from "Components/Icons/ArrowUpRightFromSquare";
import InfoCircle from "Components/Icons/InfoCircle";
import PenToSquare from "Components/Icons/PenToSquare";
import VersionInfo from "@/layouts/authenticated-layout/components/VersionInfo";
import PageTitle from "Components/Common/PageTitle";
import SimpleTabs from "@/pages/home/components/simple-tabs";
import SavedViewsHomePanel from "@/pages/home/components/saved-views-panel";

const DUE_AND_UPCOMING_LABELS = {
    overdue: "Overdue",
    next_month: "Next month",
    three_month: "Next 3 months",
};

export default function HomePage() {
    const [actionPlanFilter, setActionPlanFilter] = useState<string>("myItems");
    const { dataFavorites, error: favoriteDashboardsError, isLoading: isLoadingFavoriteDashboards } = useDashboardsInCategories();
    const { data: studentsAtRisk, error: studentsAtRiskError, isLoading: isLoadingStudentsAtRisk } = useStudentsAtRisk();
    const { data: recentDashboards, error: recentDashboardsError, isLoading: isLoadingRecentDashboards } = useRecentDashboards();
    const { data: dueItems, error: dueItemsError, isLoading: isLoadingDueItems } = useDueAndUpcomingActionPlans(actionPlanFilter);
    const { data: studentAcademicAdvising } = useDashboardByName("Student Academic Advising");
    const { data: canvasStudentAdvising } = useDashboardByName("Canvas-Student Advising");
    const { data: schoolSettings } = useSchoolSettings();
    const atRiskLinksRef = useRef<HTMLAnchorElement[]>([]);
    const favoritesLinksRef = useRef<HTMLAnchorElement[]>([]);
    const dueLinksRef = useRef<HTMLAnchorElement[]>([]);
    const dashboardLinksRef = useRef<HTMLAnchorElement[]>([]);

    const dashboardsArray = dataFavorites || [];

    const handleKeyDown = (e: React.KeyboardEvent<HTMLAnchorElement>) => {
        if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            (e.target as HTMLAnchorElement).click();
        } else if (e.key === "ArrowDown") {
            e.preventDefault();
            const currentIndex = atRiskLinksRef.current.findIndex((el) => el === document.activeElement);
            const nextIndex = (currentIndex + 1) % atRiskLinksRef.current.length;
            atRiskLinksRef.current[nextIndex]?.focus();
        } else if (e.key === "ArrowUp") {
            e.preventDefault();
            const currentIndex = atRiskLinksRef.current.findIndex((el) => el === document.activeElement);
            const prevIndex = (currentIndex - 1 + atRiskLinksRef.current.length) % atRiskLinksRef.current.length;
            atRiskLinksRef.current[prevIndex]?.focus();
        }
    };

    const handleFavoriteKeyDown = (e: React.KeyboardEvent<HTMLAnchorElement>) => {
        if (!favoritesLinksRef.current.length) return;

        const links = favoritesLinksRef.current;
        const currentIndex = links.findIndex((el) => el === document.activeElement);

        if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            e.currentTarget.click();
        } else if (e.key === "ArrowDown") {
            e.preventDefault();
            const nextIndex = (currentIndex + 1) % links.length;
            links[nextIndex]?.focus();
        } else if (e.key === "ArrowUp") {
            e.preventDefault();
            const prevIndex = (currentIndex - 1 + links.length) % links.length;
            links[prevIndex]?.focus();
        }
    };

    const getDueDateQueryParameter = (item) => {
        const dateNow = new Date();

        const tomorrow = new Date(dateNow);
        tomorrow.setDate(tomorrow.getDate() + 1);
        tomorrow.setMilliseconds(0);

        const nextMonth = new Date(dateNow);
        nextMonth.setMonth(nextMonth.getMonth() + 1);
        nextMonth.setMilliseconds(0);

        const threeMonths = new Date(dateNow);
        threeMonths.setMonth(threeMonths.getMonth() + 3);
        threeMonths.setMilliseconds(0);

        dateNow.setMilliseconds(0);

        if (item.as_due_type === "overdue") {
            return `&evaluation_end=${dateNow.toISOString()}`;
        } else if (item.as_due_type === "next_month") {
            return `&evaluation_start=${dateNow.toISOString()}&evaluation_end=${nextMonth.toISOString()}`;
        } else if (item.as_due_type === "three_month") {
            return `&evaluation_start=${dateNow.toISOString()}&evaluation_end=${threeMonths.toISOString()}`;
        }
        return "";
    };

    const getActionPlanUrl = (item) => {
        const currentUserName = Utils.getChosenSchoolInfo().name;
        const allDueActionPlansUrl = Utils.getLinkBase() + "/actionplans?overdue=true";
        const myDueActionPlansUrl = Utils.getLinkBase() + "/actionplans?overdue=true&query=" + currentUserName + "&key=assignee_name";
        const timeQuery = getDueDateQueryParameter(item);

        if (actionPlanFilter === "myItems") {
            return myDueActionPlansUrl + timeQuery;
        } else {
            return allDueActionPlansUrl + timeQuery;
        }
    };

    useEffect(() => {
        Utils.setViewName("Home", "Home");
    }, []);

    useEffect(() => {
        favoritesLinksRef.current = [];
    }, [dataFavorites]);

    useEffect(() => {
        dueLinksRef.current = [];
    }, [dueItems]);

    useEffect(() => {
        dashboardLinksRef.current = [];
    }, [recentDashboards]);


    return (
        <>
            <PageTitle pageTitle="Home" />
            <h3 className="mb-4 fw-bold">Home</h3>
            <div
                className="border-bottom"
                style={{
                position: "absolute",
                top: 124,
                left: 0,
                right: 0,
                height: "1px",
                zIndex: 1
            }}/>
            <div className="mb-4 row g-4">
                {/* At Risk and Favorites Column */}
                <div className="col-5 d-flex flex-column gap-4">
                    {/* At Risk Section */}
                    {(studentAcademicAdvising || canvasStudentAdvising) && (
                        <Card className="rounded-4" aria-live="polite">
                            <Card.Header
                                className="bg-light rounded-top-4 d-flex justify-content-between align-items-center py-2 px-3">
                                <div>
                                    <h6
                                        className="mb-0 mt-1 fw-bold"
                                        style={{
                                            fontSize: "18px",
                                            lineHeight: "26px"
                                        }}>
                                        <span className="d-flex align-items-center">
                                            Students at Risk
                                            {Utils.hasAdminAccess() && (
                                                <Badge
                                                    as={Link} to={`${Utils.getLinkBase()}/preferences`}
                                                    bg="danger-light"
                                                    tabIndex={0}
                                                    role="link"
                                                    aria-label={`Passing grade: ${schoolSettings?.at_risk_threshold}`}
                                                    onClick={() => {
                                                        window.location.href = `${Utils.getLinkBase()}/preferences`;
                                                    }}
                                                    onKeyDown={(e) => {
                                                        if (e.key === "Enter" || e.key === " ") {
                                                            e.preventDefault();
                                                            window.location.href = `${Utils.getLinkBase()}/preferences`;
                                                        }
                                                    }}
                                                    onFocus={(e) => {
                                                        e.currentTarget.style.outline = "2px solid #F7B84F";
                                                        e.currentTarget.style.boxShadow = "0 0 4px #F7B84F";
                                                    }}
                                                    onBlur={(e) => {
                                                        e.currentTarget.style.boxShadow = "none";
                                                    }}
                                                    className="ms-2 rounded-3 fw-light d-flex align-items-center focus-visible"
                                                    style={{
                                                        outline: "none",
                                                    }}
                                                >
                                                    Passing Grade: {schoolSettings && schoolSettings?.at_risk_threshold}
                                                    <PenToSquare size={14} className="ms-2" aria-hidden="true" />
                                                </Badge>
                                                )}
                                        </span>
                                    </h6>
                                </div>
                                <div className="rounded-circle d-flex align-items-center justify-content-center"
                                    style={{
                                        backgroundColor: "#E8E8E8",
                                        border: "1px solid #D8D8D8",
                                        padding: "8px",
                                        width: "36px",
                                        height: "36px"
                                    }}>
                                    <UserGroup size={18} />
                                </div>
                            </Card.Header>

                            <Card.Body className="px-3">
                                {isLoadingStudentsAtRisk && !studentsAtRiskError && (
                                    <div className="py-3 text-center">
                                        <Spinner variant="secondary" />
                                    </div>
                                )}

                                {!isLoadingStudentsAtRisk && studentsAtRisk && studentsAtRisk.data.examsoft1 > 0 && (
                                    <Link
                                        to={`${Utils.getLinkBase()}/dashboard/Student-Academic-Advising?tab=1StudentSnapshot&Desired%20final%20average=${schoolSettings?.at_risk_threshold}&At-Risk%20Student=At%20Risk`}
                                        className="btn btn-outline-secondary d-flex justify-content-between align-items-center py-2 text-black hover-transition rounded-3 focus-visible"
                                        style={{ border: "none" }}
                                        ref={(el) => el && atRiskLinksRef.current.push(el)}
                                        onKeyDown={handleKeyDown}
                                        tabIndex={0}
                                        aria-label={`You have ${studentsAtRisk.data.examsoft1} students at risk in Examsoft Legacy. Click to learn more.`}
                                    >
                                        <div className="d-flex align-items-center gap-2">
                                            <span className="font-base-size">Examsoft</span>
                                            <OverlayTrigger
                                                overlay={<Tooltip className="dashboard-card-tooltip">Click to review students with at-risk performance in ExamSoft</Tooltip>}
                                                trigger={["hover", "focus"]}
                                            >
                                                <Button
                                                    variant="icon"
                                                    className="p-0 d-flex align-items-center"
                                                >
                                                    <InfoCircle size={18} fill="#818181" color="none" />
                                                </Button>
                                            </OverlayTrigger>
                                        </div>
                                        <Badge
                                            bg="danger-light"
                                            className="rounded-pill px-2 py-1"
                                            style={{ backgroundColor: "#F8D7DA !important", color: "#B71C1C" }}
                                        >
                                            {studentsAtRisk.data.examsoft1}
                                        </Badge>
                                    </Link>
                                )}

                                {!isLoadingStudentsAtRisk && studentsAtRisk && studentsAtRisk.data.examsoft2 > 0 && (
                                    <Link
                                        to={`${Utils.getLinkBase()}/dashboard/Student-Academic-Advising?tab=1StudentSnapshot&Desired%20final%20average=${schoolSettings?.at_risk_threshold}&At-Risk%20Student=At%20Risk`}
                                        className="btn btn-outline-secondary d-flex justify-content-between align-items-center py-2 text-black hover-transition rounded-3 focus-visible"
                                        style={{ border: "none" }}
                                        ref={(el) => el && atRiskLinksRef.current.push(el)}
                                        onKeyDown={handleKeyDown}
                                        tabIndex={0}
                                        aria-label={`You have ${studentsAtRisk.data.examsoft2} students at risk in Examsoft Enterprise. Click to learn more.`}
                                    >
                                        <div className="d-flex align-items-center gap-2">
                                            <span className="font-base-size">Examsoft</span>
                                            <OverlayTrigger
                                                overlay={<Tooltip className="dashboard-card-tooltip">Click to review students with at-risk performance in ExamSoft</Tooltip>}
                                                trigger={["hover", "focus"]}
                                            >
                                                <Button
                                                    variant="icon"
                                                    className="p-0 d-flex align-items-center"
                                                >
                                                    <InfoCircle size={18} fill="#818181" color="none" />
                                                </Button>
                                            </OverlayTrigger>
                                        </div>
                                        <Badge
                                            bg="danger-light"
                                            className="rounded-pill px-2 py-1"
                                            style={{ backgroundColor: "#F8D7DA !important", color: "#B71C1C" }}
                                        >
                                            {studentsAtRisk.data.examsoft2}
                                        </Badge>
                                    </Link>
                                )}

                                {!isLoadingStudentsAtRisk && studentsAtRisk && studentsAtRisk.data.examsoft1 > 0 && studentsAtRisk.data.examsoft2 > 0 && studentsAtRisk.data.canvas > 0 &&
                                    <hr className="my-2 border-secondary-subtle" />
                                }

                                {!isLoadingStudentsAtRisk && studentsAtRisk && studentsAtRisk.data.canvas > 0 && (
                                    <Link
                                        to={`${Utils.getLinkBase()}/dashboard/Canvas-Student-Advising?tab=1StudentGradebook&Set%20Desired%20Average=${schoolSettings?.at_risk_threshold}&Section%20(At%20Risk)%20(Grade%20Score)%20(use%20range)=at%20risk`}
                                        className="btn btn-outline-secondary d-flex justify-content-between align-items-center py-2 text-black hover-transition rounded-3 focus-visible"
                                        style={{ border: "none" }}
                                        ref={(el) => el && atRiskLinksRef.current.push(el)}
                                        onKeyDown={handleKeyDown}
                                        tabIndex={0}
                                        aria-label={`You have ${studentsAtRisk.data.canvas} students at risk in Canvas. Click to learn more.`}
                                    >
                                        <div className="d-flex align-items-center gap-2">
                                            <span className="font-base-size">Canvas</span>
                                            <OverlayTrigger
                                                overlay={<Tooltip className="dashboard-card-tooltip">Click to review students with at-risk performance in Canvas</Tooltip>}
                                                trigger={["hover", "focus"]}
                                            >
                                                <Button variant="icon" className="p-0 d-flex align-items-center">
                                                    <InfoCircle size={18} fill="#818181" color="none" />
                                                </Button>
                                            </OverlayTrigger>
                                        </div>
                                        <Badge
                                            bg="danger-light"
                                            className="rounded-pill px-2 py-1"
                                            style={{ backgroundColor: "#F8D7DA !important", color: "#B71C1C" }}
                                        >
                                            {studentsAtRisk.data.canvas}
                                        </Badge>
                                    </Link>
                                )}

                                {!isLoadingStudentsAtRisk && studentsAtRisk && studentsAtRisk.data.examsoft1 === 0 && studentsAtRisk.data.examsoft2 === 0 && studentsAtRisk.data.canvas === 0 && (
                                    <div className="text-center py-3 my-4">
                                        <h3 className="h5 mb-3">No students at risk</h3>
                                        <p className="text-muted mx-auto" style={{ maxWidth: "400px" }}>
                                            All students are currently performing well. Keep monitoring regularly to ensure
                                            their continued success.
                                        </p>
                                    </div>
                                )}
                            </Card.Body>
                        </Card>
                    )}

                    {/* Favorites Section */}
                    <Card
                        className="rounded-4"
                    >
                        <Card.Header className="bg-light rounded-top-4 d-flex justify-content-between align-items-center py-2 px-3">
                            <div>
                                <h6 className="mb-0 mt-1 fw-bold" style={{ fontSize: "18px", lineHeight: "26px" }}>
                                    Favorite Dashboards
                                </h6>
                            </div>
                            <div className="rounded-circle d-flex align-items-center justify-content-center"
                                style={{
                                    backgroundColor: "#E8E8E8",
                                    border: "1px solid #D8D8D8",
                                    padding: "8px",
                                    width: "36px",
                                    height: "36px"
                                }}>
                                <Star className="text-muted" size={18} fill="#5C5C5C" />
                            </div>
                        </Card.Header>

                        <Card.Body className="px-3" style={{ maxHeight: "270px", overflowY: "auto" }}>
                            {dashboardsArray.length > 0 ? (
                                <div className="d-flex flex-column">
                                    {dashboardsArray.map((dashboard, index) => (
                                        <Fragment key={dashboard.id}>
                                            {index > 0 && <hr className="my-2 border-secondary-subtle" />}
                                            <Link
                                                to={`${Utils.getLinkBase()}/dashboard/${Utils.getUrlFriendlyString(dashboard.name)}`}
                                                className="btn btn-outline-secondary d-flex justify-content-between align-items-center py-2 text-black hover-transition rounded-3 focus-visible"
                                                style={{ border: "none" }}
                                                ref={(el) => el && favoritesLinksRef.current.push(el)}
                                                onKeyDown={handleFavoriteKeyDown}
                                                tabIndex={0}
                                                aria-label={`${dashboard.name} is on your favorite dashboard list.`}
                                            >
                                                <div className="d-flex align-items-center gap-2">
                                                    <span className="font-base-size">{dashboard.name}</span>
                                                </div>
                                                <Star className="text-warning flex-shrink-0" size={18} fill="#FFC107" />
                                            </Link>
                                        </Fragment>
                                    ))}
                                </div>
                            ) : (!isLoadingFavoriteDashboards && dashboardsArray.length === 0 && (
                                    <div className="text-center py-5 my-4">
                                        <h3 className="h5 mb-3">No Favorite Dashboards</h3>
                                        <p className="text-muted mx-auto" style={{ maxWidth: "400px" }}>
                                            You haven&apos;t added any dashboards to your favorites list yet.
                                        </p>
                                    </div>
                                )
                            )}

                            {isLoadingFavoriteDashboards && !favoriteDashboardsError && (
                                <div className="py-3 text-center">
                                    <Spinner variant="secondary" />
                                </div>
                            )}
                        </Card.Body>
                    </Card>
                </div>

                {/* Due Items Column */}
                <div className="col-4">
                    <Card className="rounded-4">
                        <Card.Header
                            className="bg-light rounded-top-4 d-flex justify-content-between align-items-start py-2 px-3">
                            <div>
                                <span
                                    className="text-muted small"
                                    style={{
                                        fontSize: "12px",
                                        fontWeight: "400",
                                        lineHeight: "16px"
                                    }}>ActionPlans</span>
                                <h6
                                    className="mb-0 mt-1 fw-bold"
                                    style={{
                                        fontSize: "18px",
                                        lineHeight: "26px"
                                    }}>Due items</h6>
                            </div>
                            <div
                                className="d-flex align-items-start justify-content-center">
                                <SimpleTabs
                                    options={[
                                        { id: "myItems", label: "My Items" },
                                        { id: "allItems", label: "All Items" },
                                    ]}
                                    selected={actionPlanFilter}
                                    onChange={(id) => setActionPlanFilter(id)}
                                />
                            </div>
                        </Card.Header>
                        <Card.Body className="px-3">
                            {dueItems && dueItems.length > 0 && dueItems.map((item, index) => (
                                <Fragment key={item.as_due_type}>
                                    {index > 0 && <hr className="my-2 border-secondary-subtle"/>}
                                    <Link
                                        to={getActionPlanUrl(item)}
                                        className="d-flex justify-content-between align-items-start py-2 text-black hover-transition rounded"
                                    >
                                        <span className="font-base-size">{DUE_AND_UPCOMING_LABELS[item.as_due_type]}</span>
                                        <Badge bg="secondary-light" className="rounded-pill px-2 py-1"
                                               style={{backgroundColor: "#E9ECEF !important", color: "#495057"}}>{item.count}</Badge>
                                    </Link>
                                </Fragment>
                            ))}

                            {dueItems && dueItems.length === 0 && (
                                <div className="text-center py-3 my-4">
                                    <h3 className="h5 mb-3">You&apos;re all caught up!</h3>
                                    <p className="text-muted mx-auto" style={{maxWidth: "400px"}}>
                                        There are no pending tasks or deadlines at the moment. Create new action plans or
                                        review completed tasks to stay organized.
                                    </p>
                                </div>
                            )}

                            {isLoadingDueItems && !dueItemsError && (
                                <div className="py-3 text-center" >
                                    <Spinner variant="secondary" />
                                </div>
                            )}
                        </Card.Body>
                    </Card>

                    {/* Recents Section */}
                    <Card className="mt-4 rounded-4">
                        <Card.Header
                            className="bg-light rounded-top-4 d-flex justify-content-between align-items-center py-2 px-3">
                            <div>
                                <span
                                    className="text-muted small"
                                    style={{
                                        fontSize: "12px",
                                        fontWeight: "400",
                                        lineHeight: "16px"
                                    }}>Dashboards</span>
                                <h6
                                    className="mb-0 mt-1 fw-bold"
                                    style={{
                                        fontSize: "18px",
                                        lineHeight: "26px"
                                    }}>Recents</h6>
                            </div>
                            <div
                                className="bg-dark-light rounded-circle p-2 d-flex align-items-center justify-content-center"
                                style={{
                                    backgroundColor: "#E8E8E8",
                                    border: "1px solid #D8D8D8",
                                    padding: "8px",
                                    width: "36px",
                                    height: "36px"
                                }}>
                                <ClockRotateLeft size={20}/>
                            </div>
                        </Card.Header>
                        <Card.Body className="px-3">
                            {recentDashboards && recentDashboards.data.length > 0 && (
                                <div className="d-flex flex-column">
                                    {recentDashboards.data.map((item, index) => (
                                        <Fragment key={item.dashboard_name}>
                                            {index > 0 && <hr className="my-2 border-secondary-subtle"/>}
                                            <Link
                                                to={`${Utils.getLinkBase()}/dashboard/${Utils.getUrlFriendlyString(item.dashboard_name)}`}
                                                className="d-flex justify-content-between align-items-start py-2 text-black hover-transition rounded">
                                            <span className="font-base-size pe-2" style={{
                                                maxWidth: "100%",
                                                display: "-webkit-box",
                                                WebkitLineClamp: 2,
                                                WebkitBoxOrient: "vertical",
                                                overflow: "hidden"
                                            }}>{item.dashboard_name}</span>
                                            </Link>
                                        </Fragment>
                                    ))}
                                </div>
                            )}

                            {recentDashboards && recentDashboards.data.length === 0 && <div className="text-center py-2 my-4">
                                <h3 className="h5 mb-3">No recent dashboards</h3>
                                <p className="text-muted mx-auto font-base-size" style={{maxWidth: "400px"}}>
                                    You haven’t opened any dashboards recently. Start exploring to see your most recent
                                    activity here.
                                </p>
                            </div>}

                            {isLoadingRecentDashboards && !recentDashboardsError && (
                                <div className="py-3 text-center" >
                                    <Spinner variant="secondary" />
                                </div>
                            )}
                        </Card.Body>
                    </Card>
                </div>

                {/* Quicklinks Column */}
                <div className="col-3">
                    <Card className="rounded-4">
                        <Card.Header
                            className="bg-light rounded-top-4 d-flex justify-content-between align-items-center py-2 px-3">
                            <h6
                                className="mb-0 mt-1 fw-bold"
                                style={{
                                    fontSize: "18px",
                                    lineHeight: "26px"
                                }}>Quick links</h6>
                            <div className="rounded-circle d-flex align-items-center justify-content-center"
                                style={{
                                    backgroundColor: "#E8E8E8",
                                    border: "1px solid #D8D8D8",
                                    padding: "8px",
                                    width: "36px",
                                    height: "36px"
                                }}>
                                <ArrowUpRightFromSquare size={18} />
                            </div>
                        </Card.Header>
                        <Card.Body className="px-3">
                            <div className="d-flex flex-column gap-2">
                                {Utils.hasAdminAccess() && (
                                    <Link to={`${Utils.getLinkBase()}/reports/create`}
                                        className="btn btn-outline-secondary d-flex align-items-center justify-content-center gap-2 rounded-3 py-2 text-decoration-none">
                                        <Plus size={18} />
                                        <span style={{
                                            fontSize: "12px",
                                            fontWeight: "700",
                                            lineHeight: "16px"
                                        }}>New report</span>
                                    </Link>
                                )}
                                <Link to={`${Utils.getLinkBase()}/actionplans?showQuickActionPlan=true`}
                                    className="btn btn-outline-secondary d-flex align-items-center justify-content-center gap-2 rounded-3 py-2 text-decoration-none">
                                    <Plus size={18} />
                                    <span style={{
                                        fontSize: "12px",
                                        fontWeight: "700",
                                        lineHeight: "16px"
                                    }}>New ActionPlan item</span>
                                </Link>
                            </div>
                        </Card.Body>
                    </Card>

                    {/* Saved Views Section */}
                    <div className="mt-4">
                        <SavedViewsHomePanel />
                    </div>
                </div>
            </div>
        <VersionInfo />
        </>
    );
}

